import React from "react";

export default function EVToolsBlogEn() {
  return (
    <>
      <p>
        In conversations with friends and family I keep hearing the same arguments for not driving an electric car.
        The first is the question of where to charge. If you own a house with a driveway or garage, setting up a
        home charger is rarely a problem — but if you rent in an apartment building, it quickly becomes one. You
        could always charge at work, of course — if that option existed.
      </p>
      <p>
        The second argument is that charging on a longer trip takes too long and the range is too short. In practice this
        is barely an issue for me anymore, but before I bought an EV I wondered the same thing. By now I know that
        I manage perfectly well in everyday life and on holiday too. Try the calculator above and see how long it
        actually takes to charge from 20 % to 80 %. Depending on battery size, that already gets you quite a long
        way until you anyways need a break yourself. Below I have a few thoughts on how these barriers could be broken down.
      </p>

      <p><strong>Expanding charging infrastructure — where people already stop</strong></p>

      <p><strong>Petrol stations</strong></p>
      <p>
        Petrol stations are already a key piece of mobility infrastructure and therefore ideal candidates for adding
        charging points. They are widespread, have grid connections, and are visited regularly. Unfortunately this
        is not yet the case everywhere — many stations offer no charging at all, or only slow options.
      </p>

      <p><strong>Workplaces and businesses</strong></p>
      <p>
        Many people would prefer to charge where their car already sits — at work or at home. Employers with their
        own car parks can make everyday charging straightforward. In practice this potential often goes untapped:
        workplace charging points are rare, and clear incentives or requirements to change that are missing.
      </p>

      <p><strong>Car parks and public parking areas</strong></p>
      <p>
        Multi-storey car parks and large parking areas are used by many people every day — often for extended
        periods. That makes them particularly well suited for charging infrastructure. Yet they are frequently
        under-equipped. Even newly built car parks are often constructed without charging technology or even
        pre-wiring. Retrofitting is costlier and more complex.
      </p>

      <p><strong>Fair and transparent charging costs</strong></p>

      <p><strong>Uniform prices per charging station</strong></p>
      <p>
        Anyone charging in public today is often confronted with a jungle of tariffs: prices vary significantly
        depending on the charging card or provider — and those paying without a card frequently pay considerably
        more. This creates uncertainty and puts people off, since it is difficult to work out the actual cost of a
        charging session in advance.
      </p>
      <p>
        Every charging station should publish a single station price per kWh that applies to everyone, regardless
        of payment method. Payment by credit or debit card should also be possible at every public charger without
        a surcharge — it works that way at petrol stations too. Charging card providers could still offer their own
        deals on top — such as lower rates via a monthly fee — as long as the base price remains transparent to all.
      </p>

      <p><strong>Dynamic pricing and a price cap</strong></p>
      <p>
        The price of electricity on the wholesale market fluctuates significantly throughout the day — cheap periods
        tend to arise when a lot of renewable energy is available. Charging tariffs could take advantage of this
        dynamic: those who charge at off-peak times would pay less. At the same time, protection against
        excessively high prices is needed.
      </p>

      <p><strong>Price display — following the Danish model</strong></p>
      <p>
        Fuel prices at petrol stations are clearly displayed — this builds trust and enables comparison. A
        comparable requirement for charging stations is still largely absent. Denmark has already successfully
        introduced such a display obligation.
      </p>
      <figure className="blog-fig">
        <img src="/images/ev-blog-danish-price-sign.jpeg" alt="Danish filling station with high power charger and price sign" className="blog-img" />
        <figcaption>Danish filling station with high power charger and price sign</figcaption>
      </figure>

      <p>
        At many charging points in Germany the price is only visible after logging in or during the charging
        session — not before, and often only via an app. At larger charging locations (from 4 points upwards)
        price boards should be prominently displayed, analogous to the fuel price boards at petrol stations.
        Individual chargers should therefore show the current price directly on the device — readable even in the
        dark. This pricing data should also be available via open interfaces so that navigation and
        charge-planning apps can always display up-to-date information.
      </p>
    </>
  );
}
